import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { getErrorMessage } from '@/lib/errors';
import { NOTIFICATION_TARGETS_KEY, monitoringApi } from '@/lib/monitoring-api';
import { relativeTime } from '@/modules/monitoring/format';

/**
 * Where alerts go.
 *
 * ## The channel list comes from the server
 *
 * It is read from the registry rather than hard-coded here, which is what lets a new channel —
 * Slack, SMS — appear in this form without a frontend change.
 *
 * ## The email caveat is stated, not hidden
 *
 * No SMTP transport is configured yet, so email alerts are written to the platform's log rather
 * than sent. A UI that let somebody configure an email address and quietly never used it would be
 * the exact failure that makes a monitoring product worthless, so it says so plainly.
 */
export function NotificationTargets() {
  const queryClient = useQueryClient();
  const { permitsChanges } = useSubscriptionAccess();

  const [channel, setChannel] = useState('email');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const targets = useQuery({
    queryKey: NOTIFICATION_TARGETS_KEY,
    queryFn: () => monitoringApi.notificationTargets(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_TARGETS_KEY });
  };

  const add = useMutation({
    mutationFn: () => monitoringApi.addNotificationTarget(channel, address.trim()),
    onSuccess: () => {
      setAddress('');
      setError(null);
      invalidate();
    },
    onError: (mutationError) => setError(getErrorMessage(mutationError)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      monitoringApi.setNotificationTargetEnabled(id, enabled),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => monitoringApi.removeNotificationTarget(id),
    onSuccess: invalidate,
  });

  const data = targets.data;
  const selected = data?.channels.find((c) => c.name === channel);

  return (
    <Card>
      <CardHeader>
        {/* No description. "Where to send alerts" already says it, and the sentence underneath
            was enumerating the three events that trigger one, which nobody needs before adding an
            email address. */}
        <CardTitle>Where to send alerts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Asked of the server rather than assumed. This was hard-coded to show whenever the
            method was "email", so it kept telling customers their alerts went nowhere while
            Postmark was configured and delivering. A warning that is wrong teaches people to
            ignore the ones that are right. */}
        {selected?.deliverable === false && (
          <Alert variant="warning">
            {selected.label} delivery is not configured on this installation yet. Alerts are
            written to the platform&rsquo;s log instead of being sent. Anything you add here starts
            working the moment it is set up.
          </Alert>
        )}

        <div className="flex flex-wrap items-end gap-2">
          {/* A column, like the field beside it. A label and a select are both inline, so in a
              plain block the label sat to the left of the select instead of above it. */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="channel">Method</Label>
            <select
              id="channel"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={channel}
              onChange={(event) => setChannel(event.target.value)}
            >
              {data?.channels.map((option) => (
                <option key={option.name} value={option.name}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[16rem] flex-1 space-y-2">
            <Label htmlFor="address">Destination</Label>
            <Input
              id="address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          </div>

          <Button
            onClick={() => add.mutate()}
            disabled={!permitsChanges || add.isPending || address.trim() === ''}
          >
            {add.isPending ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Plus className="mr-2 size-4" />
            )}
            Add
          </Button>
        </div>

        {channel === 'webhook' && (
          <p className="text-xs text-muted-foreground">
            Webhooks must use https://. The payload names your hosts and says when they are down,
            so it is not sent unencrypted.
          </p>
        )}

        <div className="space-y-2">
          {data?.targets.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No destinations yet. Outages are still recorded and shown here, but nobody is told
              about them.
            </p>
          )}

          {data?.targets.map((target) => (
            <div
              key={target.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="muted">{target.channel_label}</Badge>
                  <span className="truncate text-sm">{target.address}</span>
                  {!target.enabled && <Badge variant="muted">Paused</Badge>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {target.last_delivery_error !== '' ? (
                    <span className="text-danger">
                      Last attempt failed: {target.last_delivery_error}
                    </span>
                  ) : target.last_delivery_at !== null ? (
                    `Last sent ${relativeTime(target.last_delivery_at)}`
                  ) : (
                    'Nothing sent yet'
                  )}
                </p>
              </div>

              <div className="flex shrink-0 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggle.mutate({ id: target.id, enabled: !target.enabled })}
                  disabled={toggle.isPending}
                >
                  {target.enabled ? 'Pause' : 'Resume'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => remove.mutate(target.id)}
                  disabled={remove.isPending}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
